<script setup lang="ts">
    import { computed } from 'vue';
    import { RouterView, useRoute } from 'vue-router';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { useKeycloak } from '@/js/composables/useKeycloak';
    import { __ } from '@/js/stores/expressions';
    import { LockIcon } from '@lucide/vue';

    const route = useRoute();
    const keycloak = useKeycloak();
    const { hasAnyPermission, hasRole, isLoadingAccount, accountData } = usePermissions();

    const isAuthorized = computed(() => {
        const permissions = route.meta.permissions;

        if (accountData.value?.tenantSlug === undefined) {
            return false;
        }

        if (isLoadingAccount.value) {
            return false;
        }

        if (route.meta.requiresRole) {
            return hasRole(route.meta.requiresRole as Parameters<typeof hasRole>[0]);
        }

        if (permissions && permissions.length > 0) {
            return hasAnyPermission(
                ...permissions.map(({ area, flag }) => [area, flag] as [typeof area, typeof flag])
            ).value;
        }

        if (route.meta.open) {
            return true;
        }

        return false;
    });

    const showUnauthorized = computed(() => {
        return !isAuthorized.value && !isLoadingAccount.value && keycloak.authenticated === true;
    });
</script>

<template>
    <RouterView v-slot="{ Component }">
        <component :is="Component" v-if="isAuthorized" />
        <div
            v-else-if="showUnauthorized"
            class="flex items-center fixed w-full top-0 left-0 justify-center h-screen p-4 sm:p-8 pl-16 sm:pl-24"
        >
            <div
                class="rounded-lg bg-white p-8 shadow-md w-full self-stretch flex items-center justify-center relative"
            >
                <div class="text-center w-full max-w-96">
                    <div class="w-32 mx-auto mb-4 text-primary-carrot/10">
                        <LockIcon class="size-full" />
                    </div>
                    <h2 class="heading-lg mb-2">
                        {{ __('not-authorized') }}
                    </h2>
                    <p class="text-body">
                        {{ __('not-authorized-description') }}
                    </p>
                </div>
            </div>
        </div>
    </RouterView>
</template>
